export interface EnhancedFetchOptions extends Omit<RequestInit, "signal" | "body"> {
    /** Timeout in milliseconds (0 means no timeout). Default: 1000 */
    timeout?: number;
    /** An external AbortSignal to allow cancellation. */
    signal?: AbortSignal;
    /**
     * Request body. If an object, it's automatically stringified as JSON and Content-Type header is set.
     */
    body?: BodyInit | null | object;
}

/**
 * 3s timeout balances responsiveness and reliability:
 * 1s is too aggressive and causes failures on slower networks or higher latency paths,
 * while 3s still prevents requests from hanging indefinitely and keeps UX responsive.
 */
const DEFAULT_TIMEOUT_MS = 3_000;

/**
 * A simplified wrapper around fetch with timeout, abort, and auto JSON handling.
 * Errors are thrown as standard Error objects with descriptive messages.
 */

export async function enhancedFetch<T = any>(
    resource: RequestInfo | URL,
    options: EnhancedFetchOptions = {},
): Promise<T> {
    const {
        timeout = DEFAULT_TIMEOUT_MS,
        signal: externalSignal,
        body,
        headers: customHeaders,
        ...fetchOptions
    } = options;

    const controller = new AbortController();
    const internalSignal = controller.signal;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const abortRequest = (message: string, isTimeout: boolean = false) => {
        if (!internalSignal.aborted) {
            const error = new Error(message);
            // Use standard 'AbortError' name for consistency, or 'TimeoutError'
            error.name = isTimeout ? "TimeoutError" : "AbortError";
            controller.abort(error);
        }
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    };

    // Link external signal
    if (externalSignal) {
        if (externalSignal.aborted) {
            // Reject immediately if already aborted
            const error = new Error("Request aborted by external signal (before fetch)");
            error.name = "AbortError";
            error.cause = externalSignal.reason;
            return Promise.reject(error);
        }
        externalSignal.addEventListener(
            "abort",
            () => {
                abortRequest("Request aborted by external signal");
            },
            { signal: internalSignal },
        ); // Auto-cleanup listener
    }

    // Setup timeout
    if (timeout > 0) {
        timeoutId = setTimeout(() => {
            abortRequest(`Request timed out after ${timeout}ms`, true);
        }, timeout);
    }

    // --- Prepare Headers & Body ---
    const requestHeaders = new Headers(customHeaders);
    let requestBody: BodyInit | null = null;

    try {
        requestBody = JSON.stringify(body);
        if (!requestHeaders.has("Content-Type")) {
            requestHeaders.set("Content-Type", "application/json");
        }
    } catch (err: unknown) {
        if (timeoutId !== null) clearTimeout(timeoutId);
        const error = new Error("Failed to stringify request body");
        if (err instanceof Error) error.cause = err;
        return Promise.reject(error);
    }

    if (requestHeaders.get("Content-Type")?.includes("application/json") && !requestHeaders.has("Accept")) {
        requestHeaders.set("Accept", "application/json");
    }

    // --- Perform Fetch ---
    try {
        const response = await fetch(resource, {
            ...(fetchOptions as RequestInit),
            headers: requestHeaders,
            body: requestBody,
            signal: internalSignal,
        });

        if (timeoutId !== null) {
            clearTimeout(timeoutId); // Clear timeout on successful response start
            timeoutId = null;
        }

        // --- Handle HTTP Errors ---
        if (!response.ok) {
            // Try to get response text for context, but don't fail if that errors
            let responseBodyText: string | null = null;
            try {
                responseBodyText = await response.text();
            } catch {
                /* ignore */
            }

            const message = `HTTP error ${response.status}: ${response.statusText}${
                responseBodyText ? ` - ${responseBodyText}` : ""
            }`;
            throw new Error(message);
        }

        // --- Handle Successful Response ---
        try {
            const jsonData = await response.json();
            return jsonData as T;
        } catch (jsonError: unknown) {
            const error = new Error("Failed to parse JSON response");
            if (jsonError instanceof Error) error.cause = jsonError;
            // (error as any).response = response; // Optionally attach response
            throw error;
        }
    } catch (error: unknown) {
        if (timeoutId !== null) {
            clearTimeout(timeoutId); // Ensure timeout cleared
            timeoutId = null;
        }

        if (error instanceof Error) {
            // If it's an AbortError/TimeoutError from our controller, re-throw it directly
            if (error.name === "AbortError" || error.name === "TimeoutError") {
                // Check if the signal's reason is the error we want to throw
                if (internalSignal.reason === error) {
                    throw error;
                }
            }
            // If it's an HTTP or Parse error we threw earlier, re-throw
            if (error.message.startsWith("HTTP error") || error.message.startsWith("Failed to parse JSON")) {
                throw error;
            }

            // Otherwise, wrap it as a generic fetch failure
            throw new Error("Request failed", { cause: error });
        }

        // Fallback for non-Error exceptions
        throw new Error("An unexpected error occurred during fetch", { cause: error });
    }
}
